/**
 * Thinking Mode UI Test Page
 * Prototype for thinking mode / reasoning UI (Cursor-inspired)
 *
 * Access: http://flownotes.localhost/?test=thinking-mode
 */

import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Check, FileText, Code, Search, Undo2, CheckCircle2, X, ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';

// Animation constants - centralized for easy tweaking
const ANIMATION_DURATION = {
  fast: 150,
  normal: 300,
  slow: 500,
};

const ANIMATION_EASING = {
  easeOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  easeInOut: 'cubic-bezier(0.4, 0, 0.6, 1)',
};

// Animation styles - defined in CSS, referenced via className

// Mock data types matching design doc
interface ThinkingSubtask {
  id: string;
  type: 'search' | 'search_files' | 'read' | 'mcp_tool' | 'tool_call';
  label: string;
  status: 'active' | 'completed';
  result?: string;
}

interface CurrentTask {
  action: string;
  filePath: string;
  content: string;
  isWriting: boolean;
}

interface CompletedFile {
  filePath: string;
  status: 'new' | 'modified';
  linesAdded: number;
  linesRemoved: number;
}

export function ThinkingModeTest() {
  const [activeTab, setActiveTab] = useState<'planning' | 'operations' | 'code-gen' | 'diffs' | 'summary' | 'full-flow'>('planning');
  const [simulationState, setSimulationState] = useState<'idle' | 'running' | 'complete'>('idle');

  const tabs = [
    { id: 'planning', label: 'Planning Phase' },
    { id: 'operations', label: 'Active Operations' },
    { id: 'code-gen', label: 'Code Generation' },
    { id: 'diffs', label: 'File Diffs' },
    { id: 'summary', label: 'Summary View' },
    { id: 'full-flow', label: 'Full Flow (Animated)' },
  ];

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="border-b p-4 bg-sidebar">
        <div className="flex items-center gap-3 mb-2">
          <a
            href="/?test=index"
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Prototype Index</span>
          </a>
        </div>
        <h1 className="text-xl font-bold mb-1">Thinking Mode UI Prototype</h1>
        <p className="text-sm text-muted-foreground mb-3">
          Cursor-inspired reasoning UI with real-time operation display
        </p>

        {/* Tab Switcher */}
        <div className="flex gap-2 flex-wrap">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id as any);
                setSimulationState('idle');
              }}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {activeTab === 'planning' && <PlanningPhaseView />}
        {activeTab === 'operations' && <ActiveOperationsView />}
        {activeTab === 'code-gen' && <CodeGenerationView />}
        {activeTab === 'diffs' && <FileDiffsView />}
        {activeTab === 'summary' && <SummaryView />}
        {activeTab === 'full-flow' && (
          <FullFlowView
            simulationState={simulationState}
            setSimulationState={setSimulationState}
          />
        )}
      </div>

      {/* Global animation styles - centralized for easy tweaking */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideInFromLeft {
          from {
            opacity: 0;
            transform: translateX(-8px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes slideInFromBottom {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideInFromTop {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .fade-in {
          animation: fadeIn ${ANIMATION_DURATION.normal}ms ${ANIMATION_EASING.easeOut};
        }
        .slide-in-from-left-2 {
          animation: slideInFromLeft ${ANIMATION_DURATION.normal}ms ${ANIMATION_EASING.easeOut};
        }
        .slide-in-from-bottom-2 {
          animation: slideInFromBottom ${ANIMATION_DURATION.normal}ms ${ANIMATION_EASING.easeOut};
        }
        .slide-in-from-top-2 {
          animation: slideInFromTop ${ANIMATION_DURATION.normal}ms ${ANIMATION_EASING.easeOut};
        }
      `}</style>
    </div>
  );
}

// Tab 1: Planning Phase
function PlanningPhaseView() {
  const previousResponse = "I can help you gather information about this todo.";
  const latestPrompt = "Gather info about this todo";

  const subtasks: ThinkingSubtask[] = [
    {
      id: '1',
      type: 'search',
      label: 'Searching: How does the AI assistant chat interface handle tool calls?',
      status: 'completed',
      result: 'Explored 1 file 3 searches'
    },
    {
      id: '2',
      type: 'search_files',
      label: 'Searching files: **/chat*.tsx',
      status: 'completed',
      result: 'Found 4 files'
    },
    {
      id: '3',
      type: 'search_files',
      label: 'Searching files: **/ai-assistant*',
      status: 'completed',
      result: 'Found 2 files'
    },
    {
      id: '4',
      type: 'read',
      label: 'Read: 2025-11-27-flows-advanced-features-and-integration-assessment.md L1-150',
      status: 'completed'
    }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-lg font-semibold mb-4">Planning Phase</h2>

      <div className="space-y-6">
        {/* Previous response - faded */}
        <div className="opacity-40 text-sm text-muted-foreground border-b pb-4">
          {previousResponse}
        </div>

        {/* Latest prompt - bright */}
        <div className="text-base font-medium text-foreground">
          {latestPrompt}
        </div>

        {/* Planning indicator */}
        <div className="text-sm text-muted-foreground animate-pulse">
          Planning next moves...
        </div>

        {/* Main action */}
        <div className="font-semibold text-base mt-2">
          Designing a "thinking mode" / reasoning UI that shows the agent's work. Researching Cursor's approach and drafting a spec:
        </div>

        {/* Subtasks */}
        <div className="ml-4 space-y-1 mt-2">
          {subtasks.map(task => (
            <Subtask key={task.id} task={task} />
          ))}
        </div>
      </div>
    </div>
  );
}

// Tab 2: Active Operations
function ActiveOperationsView() {
  const subtasks: ThinkingSubtask[] = [
    {
      id: '1',
      type: 'search',
      label: 'Searching FlowNotes documents',
      status: 'active',
      result: 'Query: "self-selection cache"'
    },
    {
      id: '2',
      type: 'search_files',
      label: 'Searching files: **/todos/*8009172599*',
      status: 'active'
    },
    {
      id: '3',
      type: 'mcp_tool',
      label: 'Running MCP: flownotes_get_todo_context',
      status: 'active',
      result: 'Todo ID: 8009172599'
    },
    {
      id: '4',
      type: 'read',
      label: 'Read: clients/greater-good/ggia/todos/8009172599-self-selection-cache-fix/8009172599-technical-reference.md',
      status: 'completed'
    }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-lg font-semibold mb-4">Active Operations</h2>

      <div className="space-y-4">
        <div className="font-semibold text-base">
          Gathering context for todo 8009172599:
        </div>

        <div className="ml-4 space-y-3">
          {subtasks.map(task => (
            <Subtask key={task.id} task={task} />
          ))}
        </div>
      </div>
    </div>
  );
}

// Tab 3: Code Generation
function CodeGenerationView() {
  const [typingProgress, setTypingProgress] = useState(0);
  const [showCursor, setShowCursor] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);
  const codeRef = useRef<HTMLDivElement>(null);

  const currentTask: CurrentTask = {
    action: 'Updating design spec with new requirements:',
    filePath: 'thinking-mode-reasoning-ui-design.md',
    content: `---
title: Thinking Mode / Reasoning UI Design
date: 2025-11-27
type: design
status: planning
priority: high
---

# Thinking Mode / Reasoning UI Design

## 🎯 Overview

**Goal:** Design a "thinking mode" UI that shows the AI agent's reasoning process, tool calls, searches, and file operations in real-time - mimicking Cursor's aesthetic as the gold standard.

## 🎨 Design Principles (Cursor-Inspired)

### 1. **Show the Work, Not Just Results**

**Cursor's Approach:**
- Shows tool calls as they happen
- Displays searches being performed
- Shows files being read
- Indicates reasoning steps
- Clean, minimal aesthetic`,
    isWriting: true
  };

  useEffect(() => {
    if (typingProgress < currentTask.content.length) {
      const timer = setTimeout(() => {
        setTypingProgress(prev => prev + 1);
      }, 5); // Faster typing (was 20ms)
      return () => clearTimeout(timer);
    } else if (typingProgress >= currentTask.content.length && !showCompleted) {
      // Show completed diff after typing finishes
      setTimeout(() => setShowCompleted(true), 500);
    }
  }, [typingProgress, currentTask.content.length, showCompleted]);

  // Auto-scroll to bottom ONLY if user is already at bottom (don't force scroll)
  useEffect(() => {
    if (codeRef.current) {
      const element = codeRef.current;
      const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 50;
      // Only auto-scroll if user is already near the bottom
      if (isNearBottom) {
        element.scrollTop = element.scrollHeight;
      }
    }
  }, [typingProgress]);

  useEffect(() => {
    const cursorTimer = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 530);
    return () => clearInterval(cursorTimer);
  }, []);

  const displayedContent = currentTask.content.slice(0, typingProgress);

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-lg font-semibold mb-4">Code Generation View</h2>

      <div className="space-y-4">
        <div className="font-semibold text-base">
          {currentTask.action}
        </div>

        {/* Code generation view - narrow with max-height and auto-scroll */}
        <div className="border border-border rounded-lg mt-4 overflow-hidden slide-in-from-top-2">
          {/* Filename header - not monospaced */}
          <div className="bg-muted/50 px-3 py-1.5 text-xs border-b border-border">
            <span className="text-sm">{currentTask.filePath}</span>
          </div>

          {/* Code content with max-height and auto-scroll */}
          <div
            ref={codeRef}
            className="bg-background p-4 font-mono text-xs overflow-auto"
            style={{ maxHeight: '150px' }}
          >
            <pre className="whitespace-pre-wrap text-foreground">
              {displayedContent}
              {showCursor && <span className="animate-pulse">|</span>}
            </pre>
          </div>
        </div>

        <div className="text-sm text-muted-foreground">
          {typingProgress < currentTask.content.length ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" />
              Writing...
            </span>
          ) : (
            <span className="flex items-center gap-2 text-green-500">
              <CheckCircle2 className="w-3 h-3" />
              File complete
            </span>
          )}
        </div>

        {/* Show diff with gutter markers after completion */}
        {showCompleted && (
          <div className="mt-6 slide-in-from-bottom-2">
            <div className="flex items-center gap-2 text-green-500 mb-3">
              <CheckCircle2 className="w-4 h-4" />
              <span className="font-semibold text-sm">Updated design spec</span>
            </div>
            <FileDiffWithGutter
              filePath={currentTask.filePath}
              status="modified"
              linesAdded={12}
              linesRemoved={2}
              content={displayedContent}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Tab 4: File Diffs - Shows completed file with gutter markers
function FileDiffsView() {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const completedFile: CompletedFile = {
    filePath: 'thinking-mode-reasoning-ui-design.md',
    status: 'modified',
    linesAdded: 12,
    linesRemoved: 2
  };

  const fileContent = `---
title: Thinking Mode / Reasoning UI Design
date: 2025-11-27
type: design
status: planning
priority: high
---

# Thinking Mode / Reasoning UI Design

## 🎯 Overview

**Goal:** Design a "thinking mode" UI that shows the AI agent's reasoning process, tool calls, searches, and file operations in real-time - mimicking Cursor's aesthetic as the gold standard.

## 🎨 Design Principles (Cursor-Inspired)

### 1. **Show the Work, Not Just Results**

**Cursor's Approach:**
- Shows tool calls as they happen
- Displays searches being performed
- Shows files being read
- Indicates reasoning steps
- Clean, minimal aesthetic`;

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-lg font-semibold mb-4">File Diffs</h2>

      <div className="space-y-4">
        <div className="font-semibold text-base fade-in">
          ✓ Updated design spec
        </div>

        {/* File diff with gutter markers - expandable */}
        <div className="slide-in-from-top-2">
          <div className="border border-border rounded-lg overflow-hidden bg-card">
            {/* Filename header */}
            <div className="bg-muted/50 px-3 py-1.5 text-xs border-b border-border flex items-center justify-between">
              <span className="text-sm">{completedFile.filePath}</span>
              <div className="flex items-center gap-2">
                <Badge variant={completedFile.status === 'new' ? 'default' : 'secondary'} className="text-xs">
                  {completedFile.status}
                </Badge>
                <span className="text-green-500 text-xs">
                  +{completedFile.linesAdded}
                </span>
                {completedFile.linesRemoved > 0 && (
                  <span className="text-red-500 text-xs">
                    -{completedFile.linesRemoved}
                  </span>
                )}
              </div>
            </div>

            {/* Code with gutter markers - expandable */}
            <div
              ref={contentRef}
              className="flex font-mono text-xs bg-background transition-all duration-300 ease-out relative"
              style={{
                maxHeight: isExpanded ? '400px' : '150px',
                overflow: 'auto',
              }}
            >
              {/* Gutter */}
              <div className="bg-muted/50 select-none border-r border-border relative">
                {fileContent.split('\n').map((_, idx) => {
                  return (
                    <div
                      key={idx}
                      className="flex items-center h-6 px-2 text-muted-foreground"
                    >
                      {idx + 1}
                    </div>
                  );
                })}
              </div>

              {/* Code content */}
              <div className="flex-1 py-1 overflow-auto relative">
                  {fileContent.split('\n').map((line, idx) => {
                    const isAdded = idx >= 4 && idx < 8;
                    const isRemoved = idx >= 8 && idx < 8 + completedFile.linesRemoved;

                    return (
                      <div
                        key={idx}
                        className={`h-6 flex pl-4 items-center relative ${
                          isAdded ? 'bg-green-500/10' : ''
                        } ${
                          isRemoved ? 'bg-red-500/10' : ''
                        }`}
                      >
                        {/* Colored line extending into text area */}
                        {(isAdded || isRemoved) && (
                          <div
                            className={`absolute left-0 top-0 bottom-0 w-0.5 ${
                              isAdded ? 'bg-green-400/80' : 'bg-red-400/80'
                            }`}
                            style={{ marginLeft: '0' }}
                          />
                        )}
                        <span className="relative z-10">{line || ' '}</span>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Expand/Collapse button */}
            <div className="border-t border-border bg-muted/30">
              <button
                onClick={handleToggle}
                className="w-full px-3 py-2 flex items-center justify-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors duration-200"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="w-4 h-4 transition-transform duration-300" />
                    <span>Collapse</span>
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 transition-transform duration-300" />
                    <span>Expand</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Tab 5: Summary View
function SummaryView() {
  const completedFiles: CompletedFile[] = [
    {
      filePath: 'frontend/src/components/test-thinking-mode.tsx',
      status: 'new',
      linesAdded: 770,
      linesRemoved: 0
    },
    {
      filePath: 'frontend/src/App.tsx',
      status: 'modified',
      linesAdded: 18,
      linesRemoved: 8
    },
    {
      filePath: 'organize-todo-folders.js',
      status: 'modified',
      linesAdded: 121,
      linesRemoved: 40
    },
    {
      filePath: 'fix-todo-folder-names.js',
      status: 'new',
      linesAdded: 244,
      linesRemoved: 0
    },
    {
      filePath: '2025-11-27-multi-document-todo-pattern-and-triggers.md',
      status: 'new',
      linesAdded: 857,
      linesRemoved: 0
    },
    {
      filePath: 'multi-document-todo-pattern.md',
      status: 'new',
      linesAdded: 97,
      linesRemoved: 0
    },
    {
      filePath: '2025-10-18-flow-jobs-concept.md',
      status: 'modified',
      linesAdded: 91,
      linesRemoved: 83
    },
    {
      filePath: '2025-11-27-flows-advanced-features-and-integration-assessment.md',
      status: 'modified',
      linesAdded: 736,
      linesRemoved: 1
    },
    {
      filePath: 'thinking-mode-reasoning-ui-design.md',
      status: 'new',
      linesAdded: 708,
      linesRemoved: 0
    }
  ];

  const handleFileClick = (filePath: string) => {
    console.log('Opening file:', filePath);
    // In real implementation, this would open the file in the main window
    alert(`Would open file: ${filePath}`);
  };

  const handleKeep = (filePath: string, e: React.MouseEvent) => {
    e.stopPropagation();
    console.log('Keep file:', filePath);
    alert(`Would keep changes for: ${filePath}`);
  };

  const handleUndo = (filePath: string, e: React.MouseEvent) => {
    e.stopPropagation();
    console.log('Undo file:', filePath);
    alert(`Would undo changes for: ${filePath}`);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-lg font-semibold mb-4">Summary View</h2>

      {/* Single container with compact file list */}
      <div className="border border-border rounded-lg overflow-hidden bg-card">
        {/* Header with actions */}
        <div className="bg-muted/50 px-3 py-2 border-b border-border flex items-center justify-between">
          <span className="text-sm font-medium">{completedFiles.length} Files</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => console.log('Undo all')}>
              <Undo2 className="w-3 h-3 mr-1" />
              Undo all
            </Button>
            <Button variant="outline" size="sm" onClick={() => console.log('Keep all')}>
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Keep All
            </Button>
            <Button size="sm" onClick={() => console.log('Review')}>
              Review
            </Button>
          </div>
        </div>

        {/* File list - compact single lines */}
        <div className="divide-y divide-border">
          {completedFiles.map((file, idx) => (
            <div
              key={idx}
              className="flex items-center gap-3 px-3 py-2 hover:bg-muted/30 transition-colors cursor-pointer group"
              onClick={() => handleFileClick(file.filePath)}
              title={file.filePath} // Tooltip with full path
            >
              {/* File icon */}
              <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />

              {/* File name (truncated) - smaller font, normal (not monospace) */}
              <span className="text-xs flex-1 truncate">
                {file.filePath.split('/').pop()}
              </span>

              {/* Line changes - green/red */}
              <div className="flex items-center gap-2 text-xs">
                {file.linesAdded > 0 && (
                  <span className="text-green-500">+{file.linesAdded}</span>
                )}
                {file.linesRemoved > 0 && (
                  <span className="text-red-500">-{file.linesRemoved}</span>
                )}
              </div>

              {/* Keep/Undo buttons */}
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => handleUndo(file.filePath, e)}
                  className="p-1 hover:bg-red-500/10 rounded text-muted-foreground hover:text-red-500 transition-colors"
                  title="Undo changes"
                >
                  <X className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => handleKeep(file.filePath, e)}
                  className="p-1 hover:bg-green-500/10 rounded text-muted-foreground hover:text-green-500 transition-colors"
                  title="Keep changes"
                >
                  <Check className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Tab 6: Full Flow (Animated) - Chat stream style
function FullFlowView({
  simulationState,
  setSimulationState
}: {
  simulationState: 'idle' | 'running' | 'complete';
  setSimulationState: (state: 'idle' | 'running' | 'complete') => void;
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [typingProgress, setTypingProgress] = useState(0);
  const [showCursor, setShowCursor] = useState(true);
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set([0]));
  const codeRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const steps = [
    { name: 'Planning', duration: 2000 },
    { name: 'Searching', duration: 3000 },
    { name: 'Reading', duration: 2000 },
    { name: 'Writing', duration: 5000 },
    { name: 'File Complete', duration: 2000 },
    { name: 'Summary', duration: 0 } // No auto-advance, stays on summary
  ];

  const completedFiles: CompletedFile[] = [
    {
      filePath: 'todo-8009172599-analysis.md',
      status: 'new',
      linesAdded: 45,
      linesRemoved: 0
    }
  ];

  const toggleStep = (stepIndex: number) => {
    setExpandedSteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stepIndex)) {
        newSet.delete(stepIndex);
      } else {
        newSet.add(stepIndex);
      }
      return newSet;
    });
  };

  // Auto-collapse completed steps (except current)
  useEffect(() => {
    if (simulationState === 'running' && currentStep > 0) {
      setExpandedSteps(prev => {
        const newSet = new Set(prev);
        // Keep current step expanded, collapse previous ones
        newSet.delete(currentStep - 1);
        newSet.add(currentStep);
        return newSet;
      });
    }
  }, [currentStep, simulationState]);

  useEffect(() => {
    if (simulationState === 'running' && currentStep < steps.length - 1) {
      // Don't auto-advance from summary (last step)
      const stepTimer = setTimeout(() => {
        if (currentStep < steps.length - 2) {
          setCurrentStep(prev => prev + 1);
          setTypingProgress(0);
        } else if (currentStep === steps.length - 2) {
          // Move to summary and mark as complete
          setCurrentStep(prev => prev + 1);
          setSimulationState('complete');
        }
      }, steps[currentStep].duration);

      return () => clearTimeout(stepTimer);
    }
  }, [simulationState, currentStep]);

  useEffect(() => {
    if (simulationState === 'running' && currentStep === 3) {
      // Typing animation during writing step - faster
      if (typingProgress < 200) {
        const timer = setTimeout(() => {
          setTypingProgress(prev => prev + 1);
        }, 5); // Faster typing
        return () => clearTimeout(timer);
      }
    }
  }, [simulationState, currentStep, typingProgress]);

  // Auto-scroll to bottom ONLY if user is already at bottom (don't force scroll)
  useEffect(() => {
    if (codeRef.current && currentStep === 3) {
      const element = codeRef.current;
      const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 50;
      // Only auto-scroll if user is already near the bottom
      if (isNearBottom) {
        element.scrollTop = element.scrollHeight;
      }
    }
  }, [typingProgress, currentStep]);

  useEffect(() => {
    if (simulationState === 'running') {
      const cursorTimer = setInterval(() => {
        setShowCursor(prev => !prev);
      }, 530);
      return () => clearInterval(cursorTimer);
    }
  }, [simulationState]);

  const mockCode = `---
title: Thinking Mode / Reasoning UI Design
date: 2025-11-27
---

# Thinking Mode / Reasoning UI Design

## 🎯 Overview

**Goal:** Design a "thinking mode" UI...`.slice(0, typingProgress);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Full Flow (Animated)</h2>
        <div className="flex gap-2">
          {simulationState === 'idle' && (
            <Button onClick={() => {
              setSimulationState('running');
              setCurrentStep(0);
              setTypingProgress(0);
            }}>
              Start Simulation
            </Button>
          )}
          {simulationState === 'running' && (
            <Button variant="outline" onClick={() => {
              setSimulationState('idle');
              setCurrentStep(0);
              setTypingProgress(0);
            }}>
              Reset
            </Button>
          )}
          {simulationState === 'complete' && (
            <Button onClick={() => {
              setSimulationState('idle');
              setCurrentStep(0);
              setTypingProgress(0);
            }}>
              Restart
            </Button>
          )}
        </div>
      </div>

      {/* Chat stream style - scrollable history */}
      <div
        ref={containerRef}
        className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto pr-4"
      >
        {/* Step indicator */}
        <div className="flex gap-2 sticky top-0 bg-background/95 backdrop-blur-sm py-2 z-10">
          {steps.map((_, idx) => (
            <div
              key={idx}
              className={`flex-1 h-2 rounded transition-colors ${
                idx < currentStep
                  ? 'bg-green-500'
                  : idx === currentStep && simulationState === 'running'
                  ? 'bg-primary animate-pulse'
                  : 'bg-muted'
              }`}
            />
          ))}
        </div>

        {/* Step 0: Planning - No box, just text */}
        {currentStep >= 0 && (
          <div className="slide-in-from-bottom-2">
            <button
              onClick={() => toggleStep(0)}
              className="w-full py-2 flex items-center justify-between hover:bg-muted/30 rounded transition-colors"
            >
              <div className="flex items-center gap-2">
                {currentStep > 0 ? (
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                ) : (
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                )}
                <span className="text-sm text-muted-foreground">Planning next moves...</span>
              </div>
              {currentStep > 0 && (
                <ChevronDown className={`w-4 h-4 transition-transform text-muted-foreground ${expandedSteps.has(0) ? 'rotate-180' : ''}`} />
              )}
            </button>
            {expandedSteps.has(0) && (
              <div className="pl-6 pb-3 space-y-2 animate-in fade-in slide-in-from-top-2">
                <div className="text-sm text-muted-foreground">
                  Planning next moves...
                </div>
                <div className="font-semibold text-base">
                  Gathering context for todo 8009172599:
                </div>
              </div>
            )}
            {!expandedSteps.has(0) && currentStep > 0 && (
              <div className="pl-6 pb-2 text-xs text-muted-foreground">
                ✓ Planned context gathering strategy
              </div>
            )}
          </div>
        )}

        {/* Step 1: Searching - No box, just text */}
        {currentStep >= 1 && (
          <div className="slide-in-from-bottom-2">
            <button
              onClick={() => toggleStep(1)}
              className="w-full py-2 flex items-center justify-between hover:bg-muted/30 rounded transition-colors"
            >
              <div className="flex items-center gap-2">
                {currentStep > 1 ? (
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                ) : currentStep === 1 ? (
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                ) : null}
                <span className="text-sm text-muted-foreground">Searching FlowNotes documents</span>
              </div>
              {currentStep > 1 && (
                <ChevronDown className={`w-4 h-4 transition-transform text-muted-foreground ${expandedSteps.has(1) ? 'rotate-180' : ''}`} />
              )}
            </button>
            {expandedSteps.has(1) && (
              <div className="pl-6 pb-3 space-y-2 animate-in fade-in slide-in-from-top-2">
                <div className="font-semibold text-base">
                  Gathering context for todo 8009172599:
                </div>
                <div className="ml-4 space-y-2">
                  <div className="slide-in-from-left-2" style={{ animationDelay: '50ms' }}>
                    <Subtask
                      task={{
                        id: '1',
                        type: 'search',
                        label: 'Searching FlowNotes documents',
                        status: currentStep > 1 ? 'completed' : 'active',
                        result: currentStep > 1 ? 'Found 3 related documents' : 'Query: "self-selection cache"'
                      }}
                    />
                  </div>
                  <div className="slide-in-from-left-2" style={{ animationDelay: '100ms' }}>
                    <Subtask
                      task={{
                        id: '2',
                        type: 'search_files',
                        label: 'Searching files: **/todos/*8009172599*',
                        status: currentStep > 1 ? 'completed' : 'active',
                        result: currentStep > 1 ? 'Found 1 file' : undefined
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
            {!expandedSteps.has(1) && currentStep > 1 && (
              <div className="pl-6 pb-2 text-xs text-muted-foreground">
                ✓ Found 3 related documents and 1 file
              </div>
            )}
          </div>
        )}

        {/* Step 2: Reading - No box, just text */}
        {currentStep >= 2 && (
          <div className="slide-in-from-bottom-2">
            <button
              onClick={() => toggleStep(2)}
              className="w-full py-2 flex items-center justify-between hover:bg-muted/30 rounded transition-colors"
            >
              <div className="flex items-center gap-2">
                {currentStep > 2 ? (
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                ) : currentStep === 2 ? (
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                ) : null}
                <span className="text-sm text-muted-foreground">Reading documents</span>
              </div>
              {currentStep > 2 && (
                <ChevronDown className={`w-4 h-4 transition-transform text-muted-foreground ${expandedSteps.has(2) ? 'rotate-180' : ''}`} />
              )}
            </button>
            {expandedSteps.has(2) && (
              <div className="pl-6 pb-3 space-y-2 animate-in fade-in slide-in-from-top-2">
                <div className="font-semibold text-base">
                  Gathering context for todo 8009172599:
                </div>
                <div className="ml-4 space-y-2">
                  <div className="slide-in-from-left-2" style={{ animationDelay: '50ms' }}>
                    <Subtask
                      task={{
                        id: '1',
                        type: 'search',
                        label: 'Searching FlowNotes documents',
                        status: 'completed',
                        result: 'Found 3 related documents'
                      }}
                    />
                  </div>
                  <div className="slide-in-from-left-2" style={{ animationDelay: '100ms' }}>
                    <Subtask
                      task={{
                        id: '2',
                        type: 'search_files',
                        label: 'Searching files: **/todos/*8009172599*',
                        status: 'completed',
                        result: 'Found 1 file'
                      }}
                    />
                  </div>
                  <div className="slide-in-from-left-2" style={{ animationDelay: '150ms' }}>
                    <Subtask
                      task={{
                        id: '3',
                        type: 'read',
                        label: 'Read: clients/greater-good/ggia/todos/8009172599-technical-reference.md',
                        status: currentStep > 2 ? 'completed' : 'active'
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
            {!expandedSteps.has(2) && currentStep > 2 && (
              <div className="pl-6 pb-2 text-xs text-muted-foreground">
                ✓ Read technical reference document
              </div>
            )}
          </div>
        )}

        {/* Step 3: Writing - No box for step, but file edit has box */}
        {currentStep >= 3 && (
          <div className="slide-in-from-bottom-2">
            <button
              onClick={() => toggleStep(3)}
              className="w-full py-2 flex items-center justify-between hover:bg-muted/30 rounded transition-colors"
            >
              <div className="flex items-center gap-2">
                {currentStep > 3 ? (
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                ) : currentStep === 3 ? (
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                ) : null}
                <span className="text-sm text-muted-foreground">Creating analysis document</span>
              </div>
              {currentStep > 3 && (
                <ChevronDown className={`w-4 h-4 transition-transform text-muted-foreground ${expandedSteps.has(3) ? 'rotate-180' : ''}`} />
              )}
            </button>
            {expandedSteps.has(3) && (
              <div className="pl-6 pb-4 space-y-4 animate-in fade-in slide-in-from-top-2">
                <div className="font-semibold text-base">
                  Creating analysis document:
                </div>
                {/* Code generation view - narrow with max-height and auto-scroll - HAS BOX */}
                <div className="border border-border rounded-lg mt-4 overflow-hidden bg-card">
                  <div className="bg-muted/50 px-3 py-1.5 text-xs border-b border-border">
                    <span className="text-sm">todo-8009172599-analysis.md</span>
                  </div>
                  <div
                    ref={codeRef}
                    className="bg-background p-4 font-mono text-xs overflow-auto"
                    style={{ maxHeight: '150px' }}
                  >
                    <pre className="whitespace-pre-wrap text-foreground">
                      {mockCode}
                      {showCursor && currentStep === 3 && <span className="animate-pulse">|</span>}
                    </pre>
                  </div>
                </div>
              </div>
            )}
            {!expandedSteps.has(3) && currentStep > 3 && (
              <div className="pl-6 pb-2 text-xs text-muted-foreground">
                ✓ Created todo-8009172599-analysis.md (+45 lines)
              </div>
            )}
          </div>
        )}

        {/* Step 4: File Complete - File edit box with gutter markers */}
        {currentStep >= 4 && (
          <div className="slide-in-from-bottom-2">
            <div className="flex items-center gap-2 text-green-500 fade-in mb-3">
              <CheckCircle2 className="w-4 h-4" />
              <span className="font-semibold text-sm">Created analysis document</span>
            </div>
            {/* File diff view with gutter markers - HAS BOX - full width in full flow */}
            <FileDiffWithGutter
              filePath={completedFiles[0].filePath}
              status={completedFiles[0].status}
              linesAdded={completedFiles[0].linesAdded}
              linesRemoved={completedFiles[0].linesRemoved}
              content={`---
title: Todo 8009172599 Analysis
date: 2025-11-27
type: analysis
status: draft
---

# Analysis: Self-Selection Cache Fix

## Context

This todo involves fixing the self-selection cache mechanism. Based on the technical reference, the issue relates to how cached responses are handled.

## Key Findings

1. The cache mechanism needs to properly handle invalidation
2. Response mapping requires updates to support new data structures
3. Error handling should be improved for edge cases

## Recommended Actions

- Update cache invalidation logic
- Implement proper response mapping
- Add comprehensive error handling`}
              maxHeight={150}
              fullWidth={true}
            />
          </div>
        )}

        {/* Final Response - Direct text output, no box, larger font */}
        {currentStep >= 5 && (
          <div className="slide-in-from-bottom-2 space-y-4">
            <div className="space-y-3 text-base leading-relaxed">
              <p>
                I've gathered context for todo 8009172599 and created an analysis document. Here's what I found:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2 text-muted-foreground">
                <li>Found 3 related documents in FlowNotes</li>
                <li>Located the technical reference file for this todo</li>
                <li>Identified key information about the self-selection cache issue</li>
              </ul>
              <p className="mt-3">
                I've created <code className="px-1.5 py-0.5 bg-muted rounded text-xs">todo-8009172599-analysis.md</code> with a detailed analysis of the information gaps and recommended next steps.
              </p>
            </div>
          </div>
        )}

        {/* Step 5: Summary - Below response, HAS BOX - matches Summary View tab */}
        {currentStep >= 5 && (
          <div className="border border-border rounded-lg overflow-hidden bg-card slide-in-from-bottom-2">
            {/* Header with actions */}
            <div className="bg-muted/50 px-3 py-2 border-b border-border flex items-center justify-between">
              <span className="text-sm font-medium">{completedFiles.length} Files</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => console.log('Undo all')}>
                  <Undo2 className="w-3 h-3 mr-1" />
                  Undo all
                </Button>
                <Button variant="outline" size="sm" onClick={() => console.log('Keep all')}>
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Keep All
                </Button>
                <Button size="sm" onClick={() => console.log('Review')}>
                  Review
                </Button>
              </div>
            </div>

            {/* File list - compact single lines */}
            <div className="divide-y divide-border">
              {completedFiles.map((file, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-muted/30 transition-colors cursor-pointer group"
                  onClick={() => console.log('Opening file:', file.filePath)}
                  title={file.filePath}
                >
                  {/* File icon */}
                  <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />

                  {/* File name (truncated) - smaller font, normal (not monospace) */}
                  <span className="text-xs flex-1 truncate">
                    {file.filePath.split('/').pop()}
                  </span>

                  {/* Line changes - green/red */}
                  <div className="flex items-center gap-2 text-xs">
                    {file.linesAdded > 0 && (
                      <span className="text-green-500">+{file.linesAdded}</span>
                    )}
                    {file.linesRemoved > 0 && (
                      <span className="text-red-500">-{file.linesRemoved}</span>
                    )}
                  </div>

                  {/* Keep/Undo buttons */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log('Undo file:', file.filePath);
                      }}
                      className="p-1 hover:bg-red-500/10 rounded text-muted-foreground hover:text-red-500 transition-colors"
                      title="Undo changes"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log('Keep file:', file.filePath);
                      }}
                      className="p-1 hover:bg-green-500/10 rounded text-muted-foreground hover:text-green-500 transition-colors"
                      title="Keep changes"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Component: File Diff with Gutter Markers
function FileDiffWithGutter({
  filePath,
  status,
  linesAdded,
  linesRemoved,
  content,
  maxHeight = 150,
  fullWidth = false
}: {
  filePath: string;
  status: 'new' | 'modified';
  linesAdded: number;
  linesRemoved: number;
  content: string;
  maxHeight?: number;
  fullWidth?: boolean;
}) {
  const lines = content.split('\n');
  // Mock: first few lines are new (green), show removed lines after line 8
  const addedLinesStart = status === 'new' ? 0 : 4; // If modified, lines 4-7 are new
  const addedLinesEnd = status === 'new' ? Math.min(6, lines.length) : 8;
  const removedLineStart = status === 'modified' ? 8 : -1; // Show removed lines starting here
  const removedLineEnd = status === 'modified' ? removedLineStart + linesRemoved : -1;

  return (
    <div className={`border border-border rounded-lg overflow-hidden bg-card ${fullWidth ? '' : 'max-w-2xl'}`}>
      {/* Filename header with diff stats - not monospaced */}
      <div className="bg-muted/50 px-3 py-1.5 text-xs border-b border-border flex items-center justify-between">
        <span className="text-sm">{filePath}</span>
        <div className="flex items-center gap-2">
          <Badge variant={status === 'new' ? 'default' : 'secondary'} className="text-xs">
            {status}
          </Badge>
          <span className="text-green-500 text-xs">
            +{linesAdded}
          </span>
          {linesRemoved > 0 && (
            <span className="text-red-500 text-xs">
              -{linesRemoved}
            </span>
          )}
        </div>
      </div>

      {/* Code with gutter markers */}
      <div className="flex font-mono text-xs bg-background relative" style={{ maxHeight: `${maxHeight}px`, overflow: 'auto' }}>
        {/* Gutter */}
        <div className="bg-muted/50 select-none border-r border-border relative">
          {lines.map((_, idx) => {
            return (
              <div
                key={idx}
                className="flex items-center h-6 px-2 text-muted-foreground"
              >
                {idx + 1}
              </div>
            );
          })}
        </div>

        {/* Code content */}
        <div className="flex-1 py-1 overflow-auto relative">
          {lines.map((line, idx) => {
            const isAdded = idx >= addedLinesStart && idx < addedLinesEnd;
            const isRemoved = removedLineStart >= 0 && idx >= removedLineStart && idx < removedLineEnd;

            return (
              <div
                key={idx}
                className={`h-6 flex pl-4 items-center relative ${
                  isAdded ? 'bg-green-500/10' : ''
                } ${
                  isRemoved ? 'bg-red-500/10' : ''
                }`}
              >
                {/* Colored vertical line extending from gutter into text */}
                {(isAdded || isRemoved) && (
                  <div
                    className={`absolute left-0 top-0 bottom-0 w-0.5 ${
                      isAdded ? 'bg-green-400/80' : 'bg-red-400/80'
                    }`}
                    style={{ marginLeft: '0' }}
                  />
                )}
                <span className="relative z-10">{line || ' '}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Shared component: Subtask
function Subtask({ task }: { task: ThinkingSubtask }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          {task.status === 'active' && (
            <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
          )}
          {task.status === 'completed' && (
            <Check className="w-3 h-3 text-green-500" />
          )}
          <span className={task.status === 'active' ? 'text-foreground' : 'text-foreground'}>
            {task.label}
          </span>
        </div>
        {task.result && (
          <div className="text-xs text-muted-foreground mt-1 ml-5">
            {task.result}
          </div>
        )}
      </div>
    </div>
  );
}

